/**
 * Single source of truth for password rules.
 *
 * The three password forms (Signup, EditorRegistration, ResetPassword) share
 * these rules, and the backend's resetPasswordWithToken (`resolvers.ts`) uses
 * the same regex. Change both together.
 *
 * Any character is allowed, and no particular symbol is required, so the
 * strong passwords browsers and password managers suggest pass: Safari's look
 * like "vybsiq-Qozzo4-bukwec", Chrome's are letters and digits. The old rule
 * allowed only letters, digits and @$!%*?& and required one of those symbols,
 * which refused every Safari suggestion with no clear way out.
 */
export const PASSWORD_REGEX = /^(?=[\s\S]*[A-Z])(?=[\s\S]*[a-z])(?=[\s\S]*\d)[\s\S]{8,}$/;

export const PASSWORD_REQUIREMENTS: ReadonlyArray<{
  label: string;
  test: (password: string) => boolean;
}> = [
  { label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { label: 'One uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { label: 'One lowercase letter', test: (p) => /[a-z]/.test(p) },
  { label: 'One number', test: (p) => /\d/.test(p) },
];

export const isPasswordValid = (password: string): boolean =>
  PASSWORD_REGEX.test(password);

/** Human-readable reason the password was rejected, or null if it passes. */
export const describePasswordProblem = (password: string): string | null => {
  if (isPasswordValid(password)) return null;
  const failed = PASSWORD_REQUIREMENTS.filter((r) => !r.test(password));
  if (failed.length === 0) return 'Password does not meet the requirements.';
  return `Password needs: ${failed.map((r) => r.label.toLowerCase()).join(', ')}.`;
};

/** Score 0-5 plus the unmet requirements, for a strength meter. */
export const scorePassword = (
  password: string
): { score: number; unmet: string[]; valid: boolean } => {
  const unmet = PASSWORD_REQUIREMENTS.filter((r) => !r.test(password)).map((r) => r.label);
  return {
    score: PASSWORD_REQUIREMENTS.length - unmet.length,
    unmet,
    valid: isPasswordValid(password),
  };
};
